import { Routes } from '@/src/common/enums/routes'
import type {
  AutoPayReviewComponentType,
  AutoPayViewScreenProps,
} from '@/src/experiences/card-payments/screens/AutoPayReview'
import { useAlertPaymentProcessingError } from '@/src/app/main/(tabs)/card/card-management/payment/_useAlertPaymentProcessingError'
import { useLogError } from '@/src/common/hooks/useLogError'
import { SchedulePaymentsApi } from '@/src/experiences/card-payments/store/api'
import { useRecurringPaymentStore } from '@/src/experiences/card-payments/store/useRecurringPaymentStore'
import { useCardAccount } from '@/src/experiences/card-accounts/hooks'
import {
  getAmountTypeLabel,
  type RecurringPaymentStrategy,
} from '@/src/experiences/card-payments/screens/AutoPaySchedule/PlatformAutoPaySchedule'
import { formatCentsAsDollars } from '@/src/common/utils/general'
import { isErrorResponse, MesaApi } from '@acre-rewards/lib'
import { formatToTextMonth } from '@/src/common/utils/date'
import { formatIsoDateStringForPaymentSchedule } from '@/src/experiences/card-payments/formatIsoDateStringForPaymentSchedule'

interface PlatformAutoPayReviewProps {
  AutoPayReviewComponent: AutoPayReviewComponentType
  autoPayViewScreenProps: AutoPayViewScreenProps
}

export function PlatformAutoPayReview(props: PlatformAutoPayReviewProps) {
  const query = useCardAccount()
  return query.data?.[0]?.id ? (
    <PlatformAutoPayReviewWithCardAccount {...props} cardAccountId={query.data[0].id} />
  ) : (
    <props.AutoPayReviewComponent loading {...props} />
  )
}

export function formatRecurringPaymentStrategy(paymentStrategy: RecurringPaymentStrategy) {
  return paymentStrategy.amountType === 'static'
    ? formatCentsAsDollars(paymentStrategy.amountCents)
    : getAmountTypeLabel(paymentStrategy)
}

function PlatformAutoPayReviewWithCardAccount({
  AutoPayReviewComponent,
  autoPayViewScreenProps,
  cardAccountId,
}: PlatformAutoPayReviewProps & {
  cardAccountId: NonNullable<ReturnType<typeof useCardAccount>['data']>[number]['id']
}) {
  const alertPaymentProcessingError = useAlertPaymentProcessingError()
  const logError = useLogError()

  const [createPaymentSchedule, { isLoading }] =
    SchedulePaymentsApi.endpoints['POST /card/:cardAccountId/payments/schedules'].useMutation()

  const { account, day, paymentStrategy } = useRecurringPaymentStore()

  return (
    <AutoPayReviewComponent
      loading={isLoading}
      account={account}
      day={day ? formatToTextMonth(new Date(day), {}) : undefined}
      paymentStrategy={formatRecurringPaymentStrategy(paymentStrategy)}
      createRecurringPayment={() => {
        void (async () => {
          try {
            if (!account?.profileId || !day) {
              alertPaymentProcessingError()
              throw new Error('Missing account or day for scheduling recurring payment', {
                cause: { account, day },
              })
            }

            const processDateTime = formatIsoDateStringForPaymentSchedule(day, () =>
              alertPaymentProcessingError(`Payment cannot be scheduled for this time`),
            )

            const { error } = await createPaymentSchedule({
              params: { cardAccountId },
              body: {
                type: 'recurring',
                profileId: account.profileId,
                processDateTime,
                ...paymentStrategy,
              },
            })

            if (error) {
              if (
                isErrorResponse(error, MesaApi['POST /card/:cardAccountId/payments/schedules']) &&
                error.status === 422
              ) {
                alertPaymentProcessingError(error.body.message)
                return
              }

              alertPaymentProcessingError('One-time payment cannot be scheduled')
              throw new Error('Recurring payment scheduling failed', { cause: error })
            }

            autoPayViewScreenProps.routerPush(Routes.CardManagementAutoPaySuccessful.route)
          } catch (error) {
            logError('Could not schedule recurring payment', error)
          }
        })()
      }}
      autoPayViewScreenProps={autoPayViewScreenProps}
    />
  )
}
